/**
 * Reusable FIE Components — Shared across all FIE screens
 */
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Info, TrendingUp, TrendingDown, Minus, ShieldCheck, ShieldAlert, Shield } from "lucide-react";
import type { ConfidenceLevel, DataSourceType, ForecastRange, DataLineage, ConfidenceMetadata, CompletenessScore } from "@/data/fie/types";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";

// ── Confidence Badge ──
export function ConfidenceBadge({ level, score }: { level: ConfidenceLevel; score?: number }) {
  const config = {
    high: { icon: ShieldCheck, className: "bg-success/10 text-success border-success/20", label: "High" },
    medium: { icon: Shield, className: "bg-warning/10 text-warning border-warning/20", label: "Medium" },
    low: { icon: ShieldAlert, className: "bg-destructive/10 text-destructive border-destructive/20", label: "Low" },
  }[level];
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={`${config.className} gap-1 font-medium`}>
      <Icon className="h-3 w-3" />
      {config.label}{score !== undefined && ` (${score})`}
    </Badge>
  );
}

// ── Source Type Indicator ──
export function SourceBadge({ type }: { type: DataSourceType }) {
  const styles: Record<DataSourceType, string> = {
    actual: "bg-success/10 text-success border-success/20",
    estimated: "bg-info/10 text-info border-info/20",
    forecasted: "bg-primary/10 text-primary border-primary/20",
    blended: "bg-warning/10 text-warning border-warning/20",
    assumption: "bg-warning/10 text-warning border-warning/20",
    pending_reconciliation: "bg-muted/10 text-muted border-muted/20",
    incomplete: "bg-destructive/10 text-destructive border-destructive/20",
  };
  return (
    <Badge variant="outline" className={`${styles[type]} text-[10px] uppercase tracking-wider font-medium`}>
      {type.replace("_", " ")}
    </Badge>
  );
}

// ── Currency Display ──
export function Currency({ value, className = "" }: { value: number; className?: string }) {
  const formatted = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value);
  return <span className={className}>{formatted}</span>;
}

// ── Range Display ──
export function RangeDisplay({ range, label }: { range: ForecastRange; label?: string }) {
  return (
    <div className="space-y-1">
      {label && <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{label}</p>}
      <p className="text-2xl font-bold tracking-tight"><Currency value={range.expected} /></p>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="text-destructive"><Currency value={range.low} /></span>
        <span>—</span>
        <span className="text-success"><Currency value={range.high} /></span>
      </div>
    </div>
  );
}

// ── Data Lineage Modal ──
export function LineageDrawer({ lineage, trigger }: { lineage: DataLineage; trigger?: React.ReactNode }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || (
          <button className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <Info className="h-3 w-3" /> Source
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sm">Data Lineage</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Source:</span>
            <SourceBadge type={lineage.sourceType} />
          </div>
          <p className="text-muted-foreground">{lineage.description}</p>
          <p className="text-xs text-muted-foreground">Last updated: {new Date(lineage.lastUpdated).toLocaleDateString()}</p>
          {lineage.contributingJobIds && lineage.contributingJobIds.length > 0 && (
            <div>
              <p className="text-xs font-medium mb-1">Contributing Jobs ({lineage.contributingJobIds.length}):</p>
              <div className="flex flex-wrap gap-1">
                {lineage.contributingJobIds.slice(0, 8).map(id => (
                  <Badge key={id} variant="outline" className="text-[10px] font-mono">{id}</Badge>
                ))}
                {lineage.contributingJobIds.length > 8 && (
                  <Badge variant="secondary" className="text-[10px]">+{lineage.contributingJobIds.length - 8} more</Badge>
                )}
              </div>
            </div>
          )}
          {lineage.assumptionsUsed && lineage.assumptionsUsed.length > 0 && (
            <div>
              <p className="text-xs font-medium mb-1">Assumptions Used:</p>
              <div className="flex flex-wrap gap-1">
                {lineage.assumptionsUsed.map(a => <Badge key={a} variant="secondary" className="text-[10px]">{a}</Badge>)}
              </div>
            </div>
          )}
          {lineage.unresolvedItems && lineage.unresolvedItems.length > 0 && (
            <div>
              <p className="text-xs font-medium text-warning mb-1">Unresolved Items:</p>
              <ul className="text-xs text-muted-foreground space-y-0.5">
                {lineage.unresolvedItems.map((u, i) => <li key={i}>• {u}</li>)}
              </ul>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Confidence Detail Panel ──
export function ConfidenceDetail({ metadata }: { metadata: ConfidenceMetadata }) {
  return (
    <div className="space-y-2 p-3 rounded-lg border bg-card">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Forecast Confidence</span>
        <ConfidenceBadge level={metadata.level} score={metadata.score} />
      </div>
      <div className="space-y-1.5">
        {metadata.factors.map((f, i) => {
          const Icon = f.impact === "positive" ? TrendingUp : f.impact === "negative" ? TrendingDown : Minus;
          const color = f.impact === "positive" ? "text-success" : f.impact === "negative" ? "text-destructive" : "text-muted-foreground";
          return (
            <div key={i} className="flex items-start gap-2 text-xs">
              <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${color}`} />
              <div>
                <span className="font-medium">{f.factor}</span>
                <span className="text-muted-foreground ml-1">— {f.description}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Completeness Score Panel ──
export function CompletenessBadge({ completeness }: { completeness: CompletenessScore }) {
  const color = completeness.overall >= 70 ? "text-success" : completeness.overall >= 40 ? "text-warning" : "text-destructive";
  const items = [
    { label: "Contract Coverage", value: completeness.contractCoverage },
    { label: "Cost Completeness", value: completeness.costCompleteness },
    { label: "Schedule Certainty", value: completeness.scheduleCertainty },
    { label: "Permit Completeness", value: completeness.permitCompleteness },
  ];

  return (
    <div className="space-y-2 p-3 rounded-lg border bg-card">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Data Completeness</span>
        <span className={`text-sm font-bold ${color}`}>{completeness.overall}%</span>
      </div>
      <div className="space-y-2">
        {items.map(item => (
          <div key={item.label} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{item.label}</span>
              <span className="font-medium">{item.value}%</span>
            </div>
            <Progress value={item.value} className="h-1" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Variance Display ──
export function VarianceCell({ value, isPercent = false }: { value: number; isPercent?: boolean }) {
  const color = value > 0 ? "text-success" : value < 0 ? "text-destructive" : "text-muted-foreground";
  const prefix = value > 0 ? "+" : "";
  const display = isPercent ? `${prefix}${value.toFixed(1)}%` : `${prefix}${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value)}`;
  return <span className={`font-medium ${color}`}>{display}</span>;
}
